import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaLinkedin, FaEnvelope, FaBriefcase, FaLaptopCode } from 'react-icons/fa';
import { useData } from '../contexts/DataContext';

const LandingWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: clamp(1.5rem, 4vw, 2.5rem) clamp(1.25rem, 3vw, 1.75rem);
  color: #ffffff;
  text-align: center;
  position: relative;
  overflow: hidden;
  z-index: 1;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 15% 20%, rgba(64, 224, 255, 0.35), transparent 55%),
                radial-gradient(circle at 80% 0%, rgba(148, 42, 255, 0.25), transparent 60%),
                linear-gradient(145deg, rgba(5, 11, 22, 0.95) 0%, rgba(10, 20, 40, 0.75) 55%, rgba(5, 11, 22, 0.95) 100%);
    z-index: -1;
    pointer-events: none;
  }

  @media (max-width: 1024px) {
    padding: clamp(2rem, 5vw, 2.5rem) 1.5rem;
  }

  @media (max-width: 768px) {
    padding: 2rem 1.25rem;
  }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-12px); }
`;

const BackgroundOrb = styled.div<{ $size: number; $top: string; $left: string; $delay?: number }>`
  position: absolute;
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  top: ${props => props.$top};
  left: ${props => props.$left};
  background: radial-gradient(circle, rgba(64, 224, 255, 0.25) 0%, rgba(64, 224, 255, 0) 70%);
  filter: blur(40px);
  opacity: 0.6;
  pointer-events: none;
  animation: ${float} 8s ease-in-out infinite;
  animation-delay: ${props => props.$delay || 0}s;
`;

const Card = styled.div`
  max-width: clamp(820px, 75vw, 1040px);
  width: 100%;
  background: rgba(10, 18, 34, 0.85);
  border: 1px solid rgba(64, 224, 255, 0.25);
  border-radius: 24px;
  padding: clamp(2.8rem, 5.5vw, 3.4rem) clamp(2.4rem, 5vw, 3.2rem);
  box-shadow: 0 45px 80px rgba(5, 11, 22, 0.65);
  display: flex;
  flex-direction: column;
  gap: clamp(1.2rem, 3vw, 1.6rem);
  position: relative;

  @media (max-width: 480px) {
    padding: 2rem 1.5rem;
    border-radius: 20px;
    gap: 1.5rem;
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(300px, 360px) minmax(420px, 1fr);
  gap: clamp(2.25rem, 4.5vw, 3.75rem);
  align-items: flex-start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: clamp(2rem, 6vw, 2.5rem);
    text-align: center;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: clamp(1rem, 2.5vw, 1.6rem);
  text-align: center;
  align-items: center;
  max-width: 360px;

  @media (max-width: 900px) {
    max-width: none;
  }
`;

const RightColumn = styled.div`
  display: grid;
  gap: clamp(1.75rem, 3.5vw, 2.2rem);
`;

const Glow = styled.div`
  position: absolute;
  inset: -30%;
  background: radial-gradient(circle, rgba(64, 224, 255, 0.18) 0%, rgba(64, 224, 255, 0) 65%);
  filter: blur(70px);
  opacity: 0.6;
  pointer-events: none;
`;

const Portrait = styled.div`
  width: clamp(220px, 26vw, 260px);
  height: clamp(220px, 26vw, 260px);
  margin: 0;
  border-radius: 50%;
  overflow: hidden;
  background: rgba(64, 224, 255, 0.12);
  border: 2px solid rgba(64, 224, 255, 0.35);
  box-shadow: 0 18px 40px rgba(64, 224, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;

  @media (max-width: 900px) {
    margin: 0 auto;
  }

  @media (max-width: 480px) {
    width: clamp(180px, 40vw, 220px);
    height: clamp(180px, 40vw, 220px);
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  span {
    font-size: 6.5rem;

    @media (max-width: 480px) {
      font-size: 5rem;
    }
  }
`;

const Heading = styled.h1`
  margin: 0;
  font-size: clamp(2.4rem, 6vw, 3.2rem);
  font-weight: 200;
  letter-spacing: -0.04em;
  text-align: inherit;
`;

const Highlight = styled.span`
  background: linear-gradient(135deg, #40e0ff 0%, #64c8ff 40%, #ffffff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const IntroCopy = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  font-size: 1rem;
  max-width: 320px;

  @media (max-width: 900px) {
    max-width: 28rem;
  }

  @media (max-width: 480px) {
    font-size: 0.9rem;
    line-height: 1.5;
    max-width: 100%;
    padding: 0 0.5rem;
  }
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: clamp(1.5rem, 3.5vw, 2rem);
  width: 100%;
`;

const OptionCard = styled.div`
  background: linear-gradient(160deg, rgba(18, 30, 52, 0.85) 0%, rgba(26, 38, 64, 0.82) 45%, rgba(20, 32, 56, 0.88) 100%);
  border: 1px solid rgba(64, 224, 255, 0.22);
  border-radius: 18px;
  padding: clamp(2rem, 4.5vw, 2.4rem);
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
  text-align: left;
  box-shadow: 0 32px 60px rgba(6, 11, 22, 0.52);

  @media (max-width: 480px) {
    padding: 1.5rem 1.25rem;
    border-radius: 16px;
    gap: 0.9rem;
  }
`;

const OptionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const OptionIcon = styled.div`
  width: 46px;
  height: 46px;
  border-radius: 14px;
  background: rgba(64, 224, 255, 0.18);
  border: 1px solid rgba(64, 224, 255, 0.35);
  color: #40e0ff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
`;

const OptionTitle = styled.h3`
  margin: 0;
  font-size: 1.28rem;
  font-weight: 650;
  color: #ffffff;
`;

const OptionDescription = styled.p`
  margin: 0;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  font-size: 0.96rem;
`;

const OptionCTA = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  align-self: flex-start;
  margin-top: 0.4rem;
  padding: 0.7rem 1.5rem;
  border-radius: 999px;
  background: linear-gradient(135deg, rgba(64, 224, 255, 0.28), rgba(64, 224, 255, 0.14));
  border: 1px solid rgba(64, 224, 255, 0.4);
  color: #ffffff;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 32px rgba(64, 224, 255, 0.25);
  }

  @media (max-width: 480px) {
    padding: 0.6rem 1.2rem;
    font-size: 0.85rem;
    gap: 0.45rem;
    align-self: stretch;
    justify-content: center;
  }
`;

const ContactRow = styled.div`
  margin-top: 1rem;
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  flex-wrap: wrap;

  @media (max-width: 900px) {
    justify-content: center;
  }
`;

const ContactButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.65rem 1.3rem;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  font-size: 0.9rem;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    background: rgba(255, 255, 255, 0.18);
    color: #ffffff;
  }

  @media (max-width: 480px) {
    padding: 0.55rem 1rem;
    font-size: 0.85rem;
    gap: 0.35rem;
  }
`;

const PageLabel = styled.p`
  margin: 0 0 0.25rem;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(64, 224, 255, 0.85);
  text-align: center;
`;

const LandingPage: React.FC = () => {
  const { profile } = useData();

  useEffect(() => {
    document.title = 'Menu | Ollie Gillyon';
  }, []);

  return (
    <LandingWrapper>
      <BackgroundOrb $size={420} $top="-10%" $left="10%" />
      <BackgroundOrb $size={360} $top="60%" $left="70%" $delay={2} />
      <BackgroundOrb $size={300} $top="75%" $left="20%" $delay={1} />
      <Card>
        <Glow />
        <PageLabel>Menu</PageLabel>
        <ContentGrid>
          <LeftColumn>
            <Portrait>
              {profile.profileImage && profile.profileImage !== '/api/placeholder/300/300' ? (
                <img src={profile.profileImage} alt={profile.name} />
              ) : (
                <span>👋</span>
              )}
            </Portrait>
            <Heading>
              <Highlight>Ollie Gillyon</Highlight>
            </Heading>
            <IntroCopy>
              Self-confessed data and design geek with a knack for turning ideas into polished digital experiences.
            </IntroCopy>
            <ContactRow>
              <ContactButton href={profile.linkedin || 'https://linkedin.com/in/ollie-gillyon'} target="_blank" rel="noreferrer">
                <FaLinkedin /> LinkedIn
              </ContactButton>
              <ContactButton href={profile.email ? `mailto:${profile.email}` : 'mailto:'}>
                <FaEnvelope /> Email
              </ContactButton>
            </ContactRow>
          </LeftColumn>
          <RightColumn>
            <OptionsGrid>
              <OptionCard>
                <OptionHeader>
                  <OptionIcon><FaBriefcase /></OptionIcon>
                  <OptionTitle>Professional Profile</OptionTitle>
                </OptionHeader>
                <OptionDescription>
                  View my career progression, education and experiences that brought me to where I am today
                </OptionDescription>
                <OptionCTA to="/">
                  Explore profile <FaArrowRight />
                </OptionCTA>
              </OptionCard>

              <OptionCard>
                <OptionHeader>
                  <OptionIcon><FaLaptopCode /></OptionIcon>
                  <OptionTitle>Website Services</OptionTitle>
                </OptionHeader>
                <OptionDescription>
                  A passion project that’s grown into something real, could I help your business?
                </OptionDescription>
                <OptionCTA to="/web-services">
                  View services <FaArrowRight />
                </OptionCTA>
              </OptionCard>
            </OptionsGrid>
          </RightColumn>
        </ContentGrid>
      </Card>
    </LandingWrapper>
  );
};

export default LandingPage;
